/**
 * Candid Parser
 * Parses raw Candid strings to JavaScript objects
 *
 * @module CandidParser
 * Sprint 32: OOA/OOD Refactoring
 */

import {
    ServiceMap,
    ServiceMapTier,
    ServiceMapDevice,
    ServiceMapConnection,
    ServiceMapGenerationStep,
    ServiceMapGenerationResponse
} from '../types/ii-agent-types';

/**
 * Interface for Candid parser
 */
export interface ICandidParser {
    parseServiceMap(candidString: string | ServiceMap): ServiceMap;
    parseServiceMapGenerationResponse(candidString: string): ServiceMapGenerationResponse;
}

/**
 * Parser for converting Candid strings to JavaScript objects
 */
export class CandidParser implements ICandidParser {

    /**
     * Parse raw Candid string to ServiceMap object
     */
    parseServiceMap(candidString: string | ServiceMap): ServiceMap {
        try {
            console.log('Parsing Candid string (length:', typeof candidString === 'string' ? candidString.length : 'object', ')');

            // If it's already an object, return it
            if (typeof candidString === 'object' && candidString !== null) {
                console.log('Already an object, returning as-is');
                return candidString;
            }

            // Extract service_name
            const serviceName = this.extractString(candidString, 'service_name') || 'Unknown Service';

            // Extract total_devices
            const totalDevices = this.extractNumber(candidString, 'total_devices');

            // Extract discovered_at
            const discoveredAt = this.extractBigInt(candidString, 'discovered_at');

            // Parse tiers
            const tiers = this.parseTiers(candidString);

            // Parse connections
            const connections = this.parseConnections(candidString);

            // Parse discovery_logs
            const discoveryLogs = this.parseDiscoveryLogs(candidString);

            const parsedMap: ServiceMap = {
                service_name: serviceName,
                discovered_at: discoveredAt,
                total_devices: totalDevices,
                tiers: tiers,
                connections: connections,
                discovery_logs: discoveryLogs
            };

            console.log('Parsed service map:', parsedMap);
            console.log('   - Service:', parsedMap.service_name);
            console.log('   - Tiers:', parsedMap.tiers.length);
            console.log('   - Connections:', parsedMap.connections.length);
            console.log('   - Logs:', parsedMap.discovery_logs.length);

            return parsedMap;

        } catch (error) {
            console.error('Error parsing Candid service map:', error);
            return this.createEmptyServiceMap('Parse Error', error as Error);
        }
    }

    /**
     * Parse raw Candid string to ServiceMapGenerationResponse
     */
    parseServiceMapGenerationResponse(candidString: string): ServiceMapGenerationResponse {
        try {
            console.log('Parsing ServiceMapGenerationResponse from Candid...');

            // Extract service_name
            const serviceName = this.extractString(candidString, 'service_name') || 'Unknown Service';

            // Parse generation_steps
            const generationSteps = this.parseGenerationSteps(candidString);

            console.log(`Parsed ${generationSteps.length} generation steps`);

            // Parse final_service_map
            const finalServiceMap = this.parseFinalServiceMap(candidString, serviceName);

            return {
                service_name: serviceName,
                generation_steps: generationSteps,
                final_service_map: finalServiceMap
            };

        } catch (error) {
            console.error('Error parsing ServiceMapGenerationResponse:', error);
            throw error;
        }
    }

    // =========================================================================
    // Private Helper Methods
    // =========================================================================

    /**
     * Extract a string field from Candid text
     */
    private extractString(text: string, field: string): string {
        const match = text.match(new RegExp(`${field}\\s*=\\s*"([^"]+)"`));
        return match ? match[1] : '';
    }

    /**
     * Extract a number field from Candid text
     */
    private extractNumber(text: string, field: string): number {
        const match = text.match(new RegExp(`${field}\\s*=\\s*(\\d+)`));
        return match ? parseInt(match[1]) : 0;
    }

    /**
     * Extract a bigint field from Candid text
     */
    private extractBigInt(text: string, field: string): bigint {
        const match = text.match(new RegExp(`${field}\\s*=\\s*([\\d_]+)`));
        return match ? BigInt(match[1].replace(/_/g, '')) : BigInt(Date.now() * 1_000_000);
    }

    /**
     * Parse tiers from Candid string
     */
    private parseTiers(candidString: string): ServiceMapTier[] {
        const tiers: ServiceMapTier[] = [];
        const tierNameMatches = [...candidString.matchAll(/tier_name\s*=\s*"([^"]+)"/g)];
        const tierLevelMatches = [...candidString.matchAll(/tier_level\s*=\s*(\d+)/g)];

        console.log(`Found ${tierNameMatches.length} tier names`);

        for (let i = 0; i < tierNameMatches.length; i++) {
            const tierName = tierNameMatches[i][1];
            const tierLevel = tierLevelMatches[i] ? parseInt(tierLevelMatches[i][1]) : i + 1;

            // Find the devices section for this tier
            const tierStart = tierLevelMatches[i].index || 0;
            const nextTierStart = tierLevelMatches[i + 1]?.index || candidString.indexOf('discovery_logs');
            const tierSection = candidString.substring(tierStart, nextTierStart);

            // Parse devices in this tier
            const devices = this.parseDevicesInTier(tierSection, tierName);

            console.log(`  Tier ${tierLevel} (${tierName}): ${devices.length} devices`);

            tiers.push({
                tier_name: tierName,
                tier_level: tierLevel,
                devices: devices
            });
        }

        return tiers;
    }

    /**
     * Parse devices within a tier section
     */
    private parseDevicesInTier(tierSection: string, defaultRole: string): ServiceMapDevice[] {
        const devices: ServiceMapDevice[] = [];

        const deviceIds = [...tierSection.matchAll(/device_id\s*=\s*"([^"]+)"/g)].map(m => m[1]);
        const ipAddresses = [...tierSection.matchAll(/ip_address\s*=\s*"([^"]+)"/g)].map(m => m[1]);
        const hostnames = [...tierSection.matchAll(/hostname\s*=\s*"([^"]+)"/g)].map(m => m[1]);
        const roles = [...tierSection.matchAll(/role\s*=\s*"([^"]+)"/g)].map(m => m[1]);

        for (let j = 0; j < deviceIds.length; j++) {
            devices.push({
                device_id: deviceIds[j],
                ip_address: ipAddresses[j] || '',
                hostname: hostnames[j] || '',
                role: roles[j] || defaultRole,
                config_files: []  // Will be populated if needed
            });
        }

        return devices;
    }

    /**
     * Parse connections from Candid string
     */
    private parseConnections(candidString: string): ServiceMapConnection[] {
        const connections: ServiceMapConnection[] = [];
        const fromDeviceMatches = [...candidString.matchAll(/from_device\s*=\s*"([^"]+)"/g)];
        const toDeviceMatches = [...candidString.matchAll(/to_device\s*=\s*"([^"]+)"/g)];
        const connectionTypeMatches = [...candidString.matchAll(/connection_type\s*=\s*"([^"]+)"/g)];
        const portMatches = [...candidString.matchAll(/port\s*=\s*opt\s*\((\d+)/g)];

        console.log(`Found ${fromDeviceMatches.length} connections`);

        for (let i = 0; i < fromDeviceMatches.length; i++) {
            connections.push({
                from_device: fromDeviceMatches[i][1],
                to_device: toDeviceMatches[i]?.[1] || '',
                connection_type: connectionTypeMatches[i]?.[1] || 'unknown',
                port: portMatches[i] ? parseInt(portMatches[i][1]) : null
            });
        }

        return connections;
    }

    /**
     * Parse discovery logs from Candid string
     */
    private parseDiscoveryLogs(candidString: string): string[] {
        const discoveryLogs: string[] = [];
        const logsStartMatch = candidString.match(/discovery_logs\s*=\s*vec\s*\{/);

        if (!logsStartMatch) {
            return discoveryLogs;
        }

        const logsStartIndex = (logsStartMatch.index || 0) + logsStartMatch[0].length;
        console.log(`Found discovery_logs at position ${logsStartMatch.index}`);

        // Extract the balanced brace section
        const logsSection = this.extractBalancedBraces(candidString, logsStartIndex);
        console.log(`discovery_logs section length: ${logsSection.length} chars`);

        // Extract all quoted strings from the logs section
        const logMatches = logsSection.matchAll(/"((?:[^"\\]|\\.)*)"/gs);
        for (const logMatch of logMatches) {
            discoveryLogs.push(this.decodeEscapedString(logMatch[1]));
        }

        console.log(`Extracted ${discoveryLogs.length} discovery log entries`);
        return discoveryLogs;
    }

    /**
     * Extract content within balanced braces
     */
    private extractBalancedBraces(text: string, startIndex: number): string {
        let endIndex = startIndex;
        let inString = false;
        let escapeNext = false;
        let braceCount = 1;

        for (let i = startIndex; i < text.length; i++) {
            const char = text[i];

            // Handle escape sequences
            if (escapeNext) {
                escapeNext = false;
                continue;
            }
            if (char === '\\') {
                escapeNext = true;
                continue;
            }

            // Track if we're inside a quoted string
            if (char === '"') {
                inString = !inString;
                continue;
            }

            // Only count braces outside of strings
            if (!inString) {
                if (char === '{') {
                    braceCount++;
                } else if (char === '}') {
                    braceCount--;
                    if (braceCount === 0) {
                        endIndex = i;
                        break;
                    }
                }
            }
        }

        return text.substring(startIndex, endIndex);
    }

    /**
     * Decode escaped string sequences
     */
    private decodeEscapedString(text: string): string {
        return text
            // Replace unicode escape sequences
            .replace(/\\u\{([0-9a-fA-F]+)\}/g, (_match, hex) => {
                return String.fromCodePoint(parseInt(hex, 16));
            })
            // Replace other escape sequences
            .replace(/\\n/g, '\n')
            .replace(/\\t/g, '\t')
            .replace(/\\\\/g, '\\');
    }

    /**
     * Parse generation steps from Candid string
     */
    private parseGenerationSteps(candidString: string): ServiceMapGenerationStep[] {
        const steps: ServiceMapGenerationStep[] = [];

        // Find the generation_steps vec block
        const stepsMatch = candidString.match(/generation_steps\s*=\s*vec\s*\{([^}]+(?:\}[^}]+)*)\}/s);
        if (!stepsMatch) {
            return steps;
        }

        const stepsContent = stepsMatch[1];

        // Match each record within the vec
        const recordRegex = /record\s*\{[^}]*graph_update\s*=\s*([^;]+);[^}]*log_message\s*=\s*"([^"]+)";[^}]*timestamp\s*=\s*"([^"]+)";[^}]*log_level\s*=\s*"([^"]+)";[^}]*\}/g;

        let match;
        while ((match = recordRegex.exec(stepsContent)) !== null) {
            const graphUpdateStr = match[1].trim();
            const logMessage = match[2];
            const timestamp = match[3];
            const logLevel = match[4];

            // Parse graph_update if not null
            const graphUpdate = graphUpdateStr === 'null' ? undefined : undefined; // TODO: Parse graph updates

            steps.push({
                timestamp,
                log_message: logMessage,
                log_level: logLevel,
                graph_update: graphUpdate
            });
        }

        return steps;
    }

    /**
     * Parse the final service map from generation response
     */
    private parseFinalServiceMap(candidString: string, serviceName: string): ServiceMap {
        const finalMapMatch = candidString.match(/final_service_map\s*=\s*record\s*\{([\s\S]+)\};?\s*\}/);

        if (finalMapMatch) {
            // Extract just the service map record content
            const mapContent = finalMapMatch[1];
            console.log('Parsing final_service_map...');
            // Use the existing parser on the extracted service map content
            return this.parseServiceMap('record { ' + mapContent + ' }');
        }

        console.warn('Could not find final_service_map, using empty map');
        return this.createEmptyServiceMap(serviceName, null);
    }

    /**
     * Create an empty service map (for error cases)
     */
    private createEmptyServiceMap(name: string, error: Error | null): ServiceMap {
        return {
            service_name: name,
            discovered_at: BigInt(Date.now() * 1_000_000),
            total_devices: 0,
            tiers: [],
            connections: [],
            discovery_logs: error ? [`Parse error: ${error.message}`] : []
        };
    }
}
